// src/Components/modules/Coordinacion/ItemTraslado.js

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, Typography, Space, Row, Col, Checkbox, Badge, Tag, Tooltip, message, Modal } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  CarOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CarFilled,
  PhoneOutlined,
  InfoOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import ConfirmacionAsignacionDialog from "../Dialog/ConfirmacionAsignacionDialog";
import StatusTag from "../../common/StatusTag";
import ColoredCard from "../../common/ColoredCard";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";
import ObservacionesDialog from "../../modules/Dialog/ObservacionesDialog";

const { Text } = Typography;

// Función para formatear fecha - para fechas sin hora, usar como fecha local
const formatearFecha = (fecha) => {
  // Para fechas tipo "2025-07-19", tratarlas como fecha local sin conversión de timezone
  return dayjs(fecha, "YYYY-MM-DD").format("DD/MM/YYYY");
};

// Funciones para obtener colores según el estado
const getStatusColor = (status) => {
  const statusColors = {
    Cancelado: "#b10e1a",
    Finalizado: "#4d4d4d",
    Fallido: "#4a2c04",
    FallidoXvalidar: "#d4380d",
    FinalizadoXvalidar: "#722ED1",
    Asignado: "#1f7362",
    "Pre-Asignado": "#0c27a0",
    Programado: "#6c06b5",
    Enterado: "#0c5b07",
    Iniciado: "#fa7114",
    Aceptado: "#088A08",
    "En Camino": "#FFBF00",
    Esperando: "#a44706",
    default: "#b0b500",
  };
  return statusColors[status] || statusColors.default;
};

const getConfirmacionColor = (confirmacion) => {
  return confirmacion === "Confirmado" ? "#52c41a" : "#f5222d"; // Verde si está confirmado, rojo si no
};

const getSeguimientoColor = (seguimiento) => {
  return seguimiento === "Validado" ? "#52c41a" : "#f5222d"; // Verde si validado, rojo si no
};

const getInicioStatusColor = (inicioStatus) => {
  const inicioStatusColors = {
    "Inicio Exitosamente": "#52c41a", // Verde
    "Ubicación No Válida para Iniciar": "#faad14", // Amarillo
    "Sin Conectividad Para Iniciar": "#faad14", // Amarillo
  };
  return inicioStatusColors[inicioStatus] || "#faad14"; // Por defecto amarillo
};

const getFinStatusColor = (finStatus) => {
  const finStatusColors = {
    "Finalizó Exitosamente": "#52c41a", // Verde
    "Ubicación No Válida para Finalizar": "#faad14", // Amarillo
    "Sin Conectividad Para Finalizar": "#faad14", // Amarillo
  };
  return finStatusColors[finStatus] || "#faad14"; // Por defecto amarillo
};

const ItemTraslado = ({
  traslado,
  handleContextMenu,
  onAsignarMovil,
  isSelected,
  onSelect,
  selectedMovil,
  selectedStatus,
}) => {
  const { user } = useUser(); // Usa el contexto de usuario
  const statusColor = getStatusColor(traslado.status);
  const confirmacionColor = getConfirmacionColor(traslado.confirmacion);
  const inicioStatusColor = getInicioStatusColor(traslado.inicio_status);
  const finStatusColor = getFinStatusColor(traslado.fin_status);
  const [observacionesOpen, setObservacionesOpen] = useState(false);
  const { user: currentUser } = useUser();

   // Función de permisos para el módulo "Coordinacion"
   const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find(
        (m) => m.key === "Coordinacion"
      );
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Coordinacion" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Estado para el diálogo de confirmación
  const [openDialog, setOpenDialog] = useState(false);
  const [movilToAssign, setMovilToAssign] = useState(null);

  // Función para manejar la apertura del modal
  const handleOpenObservaciones = (e) => {
    e.stopPropagation();
    setObservacionesOpen(true);
  };

  // Función para manejar el cierre del modal
  const handleCloseObservaciones = () => {
    setObservacionesOpen(false);
  };

  // Función para manejar errores de asignación
  const handleAsignacionError = (error, movil) => {
    if (error.response?.status === 422) {
      const data = error.response.data;
      let contenido = [];

      if (data.detalles?.documentos_vencidos?.length > 0) {
        contenido.push(
          <div key="vencidos" style={{ marginBottom: 8 }}>
            <strong>Documentos vencidos:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
              {data.detalles.documentos_vencidos.map((doc, i) => (
                <li key={i}>{doc.documento} - Vence: {doc.fecha_vencimiento}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (data.detalles?.documentos_sin_fecha?.length > 0) {
        contenido.push(
          <div key="sinfecha" style={{ marginBottom: 8 }}>
            <strong>Documentos sin fecha registrada:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
              {data.detalles.documentos_sin_fecha.map((doc, i) => (
                <li key={i}>{doc}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (data.detalle) {
        contenido.push(
          <div key="detalle">
            <strong>Conductor:</strong> {data.detalle}
            {data.fecha_vencimiento_licencia && (
              <span> (Vence: {data.fecha_vencimiento_licencia})</span>
            )}
          </div>
        );
      }

      Modal.error({
        title: `No se puede asignar el móvil ${movil.codigo}`,
        content: (
          <div>
            <p>{data.error}</p>
            <p><strong>Fecha del traslado:</strong> {data.fecha_traslado}</p>
            {contenido}
          </div>
        ),
        okText: "Entendido",
      });
    } else {
      message.error("Error al asignar el móvil. Intente nuevamente.");
    }
  };

  // Función para manejar el evento "onDrop" cuando se suelta un móvil sobre un traslado
  const handleDrop = async (e) => {
    e.preventDefault();
    const movil = JSON.parse(e.dataTransfer.getData("movil"));
    console.log("Movil arrastrado:", movil);

    // Si el traslado está en estado Pre-Asignado, permitir cambiar el móvil directamente
    if (traslado.status === "Pre-Asignado") {
      try {
        const response = await api.put(
          `/traslados/asignar-vehiculo/${traslado.id}`,
          {
            vehiculo_id: movil.id,
            nombre_vehiculo: movil.codigo,
            placa: movil.placa,
            conductor_id: movil.Conductor ? movil.Conductor.id : null,
            nombre_conductor: movil.Conductor ? movil.Conductor.nombre : null,
            usuario_id: user.id,
            status: "Pre-Asignado", // Mantener el estado Pre-Asignado
            historial: `Desasignó Móvil ${traslado.nombre_vehiculo} Y Pre-asignó móvil ${movil.codigo}`,
          }
        );

        if (typeof onAsignarMovil === "function") {
          onAsignarMovil(traslado.id, response.data.traslado);
        }
      } catch (error) {
        console.error("Error al asignar el móvil:", error);
        handleAsignacionError(error, movil);
      }
      return;
    }

    // Si el traslado está en estado Asignado, mostrar diálogo de confirmación
    if (traslado.status === "Asignado") {
      setMovilToAssign(movil);
      setOpenDialog(true);
      return;
    }

    // Para otros estados, mantener la lógica actual
    if (traslado.status !== "Programado") {
      onAsignarMovil(traslado.id, {
        error: "Solo se asignan traslados Programados",
      });
      return;
    }

    try {
      const response = await api.put(
        `/traslados/asignar-vehiculo/${traslado.id}`,
        {
          vehiculo_id: movil.id,
          nombre_vehiculo: movil.codigo,
          placa: movil.placa,
          conductor_id: movil.Conductor ? movil.Conductor.id : null,
          nombre_conductor: movil.Conductor ? movil.Conductor.nombre : null,
          usuario_id: user.id,
          status: "Asignado",
        }
      );

      if (typeof onAsignarMovil === "function") {
        onAsignarMovil(traslado.id, response.data.traslado);
      }
    } catch (error) {
      console.error("Error al asignar el móvil:", error);
      handleAsignacionError(error, movil);
    }
  };

  // Con "Actualizar origen - destino" se puede seleccionar cualquier traslado (TrasladosColumn valida la asignación)
  const puedeActualizarOrigenDestino = hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CO");

  // Lógica para deshabilitar el checkbox según las reglas
  const isCheckboxDisabled = (() => {
    if (puedeActualizarOrigenDestino) {
      return false;
    }
    if (
      traslado.status !== "Programado" &&
      traslado.status !== "Pre-Asignado"
    ) {
      return true;
    }

    if (traslado.status === "Pre-Asignado") {
      return false; // Siempre permitir selección para Pre-Asignado
    }

    if (!selectedStatus) {
      return !selectedMovil;
    } else {
      return traslado.status !== selectedStatus;
    }
  })();

  // Verificamos permisos para arrastrar y soltar
  const puedeAsignar = hasPermission("ASIGNAR_CO");
  const puedePreasignar = hasPermission("PREASIGNAR_CC");

  // Solo permitir drag and drop si tiene al menos uno de los permisos
  const permitirDragDrop = puedeAsignar || puedePreasignar;

  return (
    <ColoredCard color={statusColor}>
      <Card
        className="traslados-item"
        key={traslado.id}
        onContextMenu={(event) => handleContextMenu(event, traslado)}
        onDragOver={(e) => {
          // Si no se permite el drag and drop, no hacer nada
          if (!permitirDragDrop) {
            return;
          }
          e.preventDefault();
          const cardElement = e.currentTarget;
          const header = cardElement.querySelector(
            ".traslados-item-id-container"
          );

          // Permitir arrastre solo para estados Programado, Pre-Asignado y Asignado
          if (
            traslado.status !== "Programado" &&
            traslado.status !== "Pre-Asignado" &&
            traslado.status !== "Asignado"
          ) {
            e.dataTransfer.dropEffect = "none";
            cardElement.style.cursor = "no-drop";
            header.style.backgroundColor = "#ff4d4f";
            header.style.transition = "background-color 0.3s ease";
          } else {
            e.dataTransfer.dropEffect = "copy";
            cardElement.style.cursor = "copy";
            header.style.backgroundColor = "#0a2e5c";
          }
        }}
        onDragLeave={(e) => {
          const cardElement = e.currentTarget;
          const header = cardElement.querySelector(
            ".traslados-item-id-container"
          );
          cardElement.style.cursor = "grab";
          header.style.backgroundColor = "#0a2e5c";
        }}
        onDrop={(e) => {
          const cardElement = e.currentTarget;
          const header = cardElement.querySelector(
            ".traslados-item-id-container"
          );
          cardElement.style.cursor = "grab";
          header.style.backgroundColor = "#0a2e5c";

          // Permitir el drop solo para estados Programado, Pre-Asignado y Asignado
          if (
            traslado.status !== "Programado" &&
            traslado.status !== "Pre-Asignado" &&
            traslado.status !== "Asignado"
          ) {
            e.preventDefault();
            return;
          }
          handleDrop(e);
        }}
        hoverable
        variant="borderless"
        style={{
          padding: 0,
          cursor: "grab",
        }}
      >
        {/* Contenedor para el checkbox y el contenido */}
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Solo mostrar el checkbox si el traslado está en estado "Pre-Asignado" o "Programado" */}
          {(puedeActualizarOrigenDestino ||
            traslado.status === "Programado" ||
            traslado.status === "Pre-Asignado") && (
            <Checkbox
              checked={isSelected}
              onChange={onSelect}
              disabled={isCheckboxDisabled}
              style={{ marginRight: 8 }}
            />
          )}
          {/* Contenedor de CS-id y estado */}
          <div
            className="traslados-item-id-container"
            style={{
              backgroundColor: "#0a2e5c",
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <Text
              strong
              style={{
                color: "#fff",
                marginRight: "8px",
                wordBreak: "break-all",
              }}
            >
              CS-{traslado.id}
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {/* Tag TAI - Solo se muestra si es traslado TAI */}
              {traslado.es_tai && (
                <Tooltip title={`Traslado TAI - ${traslado.tai_trayecto_tipo || 'Sin tipo definido'}`}>
                  <Tag color="purple" icon={<CarOutlined />} style={{ margin: 0 }}>
                    TAI
                  </Tag>
                </Tooltip>
              )}

              {/* Tag de Estado del Traslado */}
              <StatusTag color={statusColor} text={traslado.status} />

              {/* Tag de Confirmación del Traslado con Icono */}
              <StatusTag
                color={confirmacionColor}
                text={
                  traslado.confirmacion === "Confirmado"
                    ? "Confirmado"
                    : "Sin Confirmar"
                }
                icon={
                  traslado.confirmacion === "Confirmado" ? (
                    <CheckCircleOutlined />
                  ) : null
                }
              />

              {/* Tag de Inicio Status - Solo se muestra si existe inicio_status */}
              {traslado.inicio_status && (
                <StatusTag
                  color={inicioStatusColor}
                  text={traslado.inicio_status}
                />
              )}

              {/* Tag de Fin Status - Solo se muestra si existe fin_status */}
              {traslado.fin_status && (
                <StatusTag color={finStatusColor} text={traslado.fin_status} />
              )}

              {/* Tag de Validacion del Traslado con Icono */}
              <StatusTag
                color={getSeguimientoColor(traslado.seguimiento)}
                text={
                  traslado.seguimiento === "Validado"
                    ? "Validado"
                    : "Sin Validar"
                }
                icon={
                  traslado.seguimiento === "Validado" ? (
                    <CheckCircleFilled />
                  ) : null
                }
              />
            </div>
          </div>
        </div>

        {/* Distribución de 3 columnas */}
        <Row gutter={[16, 16]} style={{ padding: "10px" }}>
          {/* Columna izquierda: Fechas y horas */}
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size="small">
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>
                  {formatearFecha(traslado.fecha)}
                </Text>
              </Space>
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>Hora Recogida: {traslado.hora_recogida?.slice(0, 5) || '--:--'}</Text>
              </Space>
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>Hora Llegada: {traslado.hora_llegada?.slice(0, 5) || '--:--'}</Text>
              </Space>
            </Space>
          </Col>

          {/* Columna central: Origen y destino */}
          <Col xs={24} sm={12} md={8} style={{ textAlign: "center" }}>
            <Space direction="vertical" size="small">
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.origen_lugar ? "#52c41a" : "#cccccc",
                    filter: traslado.origen_lugar
                      ? "drop-shadow(0 0 2px rgba(82, 196, 26, 0.4))"
                      : "none",
                  }}
                />{" "}
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {traslado.origen_lugar}
                  </span>{" "}
                  {traslado.origen_direccion} - {traslado.origen_ciudad}
                </Text>
              </Space>
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.destino_lugar ? "#f5222d" : "#cccccc",
                    filter: traslado.destino_lugar
                      ? "drop-shadow(0 0 2px rgba(245, 34, 45, 0.4))"
                      : "none",
                  }}
                />{" "}
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {traslado.destino_lugar}
                  </span>{" "}
                  {traslado.destino_direccion} - {traslado.destino_ciudad}
                </Text>
              </Space>
            </Space>
          </Col>

          {/* Columna derecha: Información del paciente */}
          <Col xs={24} sm={24} md={8} style={{ textAlign: "right" }}>
            <Space direction="vertical" size="small">
              <Space>
                <UserOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>{traslado.nombre_paciente}</Text>
                {hasPermission("VISUALIZAR_CO") && (
            <Badge
              count={traslado.observaciones_count || 0}
              style={{
                backgroundColor: (traslado.observaciones_count || 0) > 0 ? "#52c41a" : "#d9d9d9",
                boxShadow: (traslado.observaciones_count || 0) > 0 
                  ? "0 0 0 2px rgba(82, 196, 26, 0.2)" 
                  : "0 0 0 2px rgba(217, 217, 217, 0.2)",
                cursor: "pointer",
              }}
              title={`${traslado.observaciones_count || 0} observaciones`}
            >
              <CommentOutlined
                style={{
                  fontSize: "18px",
                  color: (traslado.observaciones_count || 0) > 0 ? "#1890ff" : "#8c8c8c",
                  filter: (traslado.observaciones_count || 0) > 0 
                    ? "drop-shadow(0 0 2px rgba(24, 144, 255, 0.2))" 
                    : "none",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onClick={handleOpenObservaciones}
              />
            </Badge>
          )}
              </Space>
              <Space>
                <Text style={{ color: "#8c8c8c" }}>
                  {traslado.tipo_documento_paciente}
                </Text>
              </Space>
              <Space>
                <IdcardOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>{traslado.numero_documento_paciente}</Text>
              </Space>
              {traslado.numero_contacto_paciente && (
                <Space>
                  <PhoneOutlined
                    style={{ fontSize: "16px", color: "#1890ff" }}
                  />
                  <Text>{traslado.numero_contacto_paciente}</Text>
                </Space>
              )}
            </Space>
          </Col>
        </Row>

        {/* Empresa y Copago */}
        <Row
          gutter={[16, 16]}
          style={{ padding: "0 10px", textAlign: "center" }}
        >
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <MedicineBoxOutlined
              style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
            />
            <Text>{traslado.empresa}</Text>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <DollarOutlined
              style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
            />
            <Text>{formatCurrencyCOP(traslado.copago_diario) || "$ 0,00"}</Text>
          </Col>
        </Row>

        {/* Trayecto, duración, distancia y observaciones */}
        <Row
          gutter={[16, 16]}
          style={{ padding: "0 10px", textAlign: "center" }}
        >
          <Col xs={24} sm={12} md={8}>
            <Space>
              <CarOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
              <Text> {traslado.trayecto}</Text>
            </Space>
          </Col>
          {traslado.observaciones_traslado && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoCircleOutlined
                  style={{ fontSize: "16px", color: "#faad14" }}
                />
                <Text style={{ color: "orange" }}>
                  {traslado.observaciones_traslado}
                </Text>
              </Space>
            </Col>
          )}
          {traslado.indicaciones_conductor && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoOutlined style={{ fontSize: "16px", color: "#faad14" }} />
                <Text style={{ color: "orange" }}>
                  {traslado.indicaciones_conductor}
                </Text>
              </Space>
            </Col>
          )}
        </Row>

        {/* Tipo de traslado, vehículo y placa */}
        <div
          className="traslados-item-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: "10px",
            padding: "10px",
          }}
        >
          {/* Tipo de viaje en el lado izquierdo */}
          <Text
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "5px",
            }}
          >
            {traslado.tipo_traslado} - {traslado.tipo_viaje}
          </Text>

          {/* Si hay vehículo asignado, mostrar en el lado derecho */}
          {traslado.nombre_vehiculo && traslado.placa && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Text style={{ display: "flex", alignItems: "center" }}>
                <CarFilled
                  style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
                />{" "}
                {traslado.nombre_vehiculo} - {traslado.placa}
              </Text>
              {traslado.nombre_conductor && (
                <Text style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
                  <UserOutlined
                    style={{ fontSize: "14px", color: "#52c41a", marginRight: 8 }}
                  />
                  {traslado.nombre_conductor}
                </Text>
              )}
            </div>
          )}
        </div>

        {/* Diálogo de confirmación */}
        <ConfirmacionAsignacionDialog
          open={openDialog}
          selectedMovil={movilToAssign}
          trasladoId={traslado.id}
          onConfirm={async () => {
            if (movilToAssign) {
              try {
                const response = await api.put(
                  `/traslados/asignar-vehiculo/${traslado.id}`,
                  {
                    vehiculo_id: movilToAssign.id,
                    nombre_vehiculo: movilToAssign.codigo,
                    placa: movilToAssign.placa,
                    conductor_id: movilToAssign.Conductor
                      ? movilToAssign.Conductor.id
                      : null,
                    nombre_conductor: movilToAssign.Conductor
                      ? movilToAssign.Conductor.nombre
                      : null,
                    usuario_id: user.id,
                    status: "Pre-Asignado",
                    historial: `Desasignó Móvil ${traslado.nombre_vehiculo} Y Pre-asignó móvil ${movilToAssign.codigo}`,
                  }
                );

                if (typeof onAsignarMovil === "function") {
                  onAsignarMovil(traslado.id, response.data.traslado);
                }
                setOpenDialog(false);
                setMovilToAssign(null);
              } catch (error) {
                console.error("Error al asignar el móvil:", error);
                handleAsignacionError(error, movilToAssign);
                setOpenDialog(false);
                setMovilToAssign(null);
              }
            } else {
              setOpenDialog(false);
              setMovilToAssign(null);
            }
          }}
          onCancel={() => {
            setOpenDialog(false);
            setMovilToAssign(null);
          }}
          mensaje="¿Estás seguro que deseas cambiar el móvil? El estado del servicio pasará a Pre-asignado."
        />
        <ObservacionesDialog
          open={observacionesOpen}
          onClose={handleCloseObservaciones}
          trasladoId={traslado.id}
          title={`Observaciones - CS-${traslado.id}`}
          key={`observaciones-${traslado.id}`}
        />
      </Card>
    </ColoredCard>
  );
};

// Validación de las props con PropTypes

ItemTraslado.propTypes = {
  traslado: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    status: PropTypes.string.isRequired,
    confirmacion: PropTypes.string.isRequired,
    inicio_status: PropTypes.string,
    fin_status: PropTypes.string,
    fecha: PropTypes.string.isRequired,
    hora_recogida: PropTypes.string.isRequired,
    hora_llegada: PropTypes.string.isRequired,
    origen_direccion: PropTypes.string.isRequired,
    origen_ciudad: PropTypes.string.isRequired,
    origen_lugar: PropTypes.string,
    destino_lugar: PropTypes.string,
    destino_direccion: PropTypes.string.isRequired,
    destino_ciudad: PropTypes.string.isRequired,
    nombre_paciente: PropTypes.string.isRequired,
    tipo_documento_paciente: PropTypes.string.isRequired,
    numero_documento_paciente: PropTypes.string.isRequired,
    trayecto: PropTypes.string.isRequired,
    duracion_estimada: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    distancia_estimada: PropTypes.number,
    observaciones_traslado: PropTypes.string,
    indicaciones_conductor: PropTypes.string,
    empresa: PropTypes.string.isRequired,
    copago_diario: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string, // Agregar esta línea
      PropTypes.oneOf([null]),
    ]),
    nombre_vehiculo: PropTypes.string,
    placa: PropTypes.string,
    nombre_conductor: PropTypes.string,
    tipo_traslado: PropTypes.string.isRequired,
    tipo_viaje: PropTypes.string.isRequired,
    seguimiento: PropTypes.string,
    calendario: PropTypes.arrayOf(PropTypes.string),
    observaciones_count: PropTypes.number,
    es_tai: PropTypes.bool,
    tai_trayecto_tipo: PropTypes.string,
    tai_estado_asignacion: PropTypes.string,
    transportadora_id: PropTypes.number,
  }).isRequired,
  handleContextMenu: PropTypes.func.isRequired,
  onAsignarMovil: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  numero_contacto_paciente: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  selectedMovil: PropTypes.object,
  selectedStatus: PropTypes.string,
};

export default ItemTraslado;
